import { useEffect } from "react";
import { useMatches, useSearchParams } from "react-router-dom";

import { appBus } from "@src/features/events";

/**
 * Bridges React Router's context into the app event bus.
 *
 * Route params and search params are only accessible inside the React tree.
 * This component sits at the root and re-emits them onto `appBus` whenever
 * they change, so the encounter actor and patient store — which live outside
 * React — can react to navigation without needing their own router hooks.
 *
 * Params come from `useMatches()` with child precedence so a layout-mounted
 * bridge still sees descendant params such as `patientId` and `encounterId`.
 *
 * @example
 * <RouterBridge />
 * <App />
 */
export const RouterBridge = () => {
  const [searchParams] = useSearchParams();
  const matches = useMatches();
  const mergedParams = matches.reduce<Record<string, string | undefined>>(
    (params, match) => Object.assign(params, match.params),
    {},
  );
  const patientId = mergedParams.patientId;
  const encounterId = mergedParams.encounterId;

  useEffect(() => {
    appBus.emit("client.router_params_changed", {
      params: { patientId, encounterId },
    });
  }, [patientId, encounterId]);

  useEffect(() => {
    appBus.emit("client.router_search_params_changed", {
      searchParams,
      params: { patientId, encounterId },
    });
  }, [searchParams, patientId, encounterId]);

  return null;
};
